import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, Gift, ShieldAlert, ArrowRight, Plus, Trash2, ChevronUp, ChevronDown, Sparkles, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { t, nameForm } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TODAY_STR = fmtDateInput(new Date());
const MAX_STR = fmtDateInput(new Date(Date.now() + 30 * 86400000));

const CopyField = ({ label, url, hint, testid, tone = "primary" }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success(t("common.linkCopied"));
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div
      className={`rounded-2xl border p-5 ${tone === "admin" ? "border-[#E8DEC2] bg-[#F8EBE6]" : "border-[#E8DEC2] bg-white"}`}
      data-testid={`${testid}-block`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-[#2C221E]">{label}</span>
      </div>
      <p className="text-sm text-[#7A6B63] mb-3">{hint}</p>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 min-w-0 rounded-xl bg-[#FDFBF7] border border-[#E8DEC2] px-3 py-3 text-sm text-[#4A3E39] break-all font-mono" data-testid={`${testid}-url`}>
          {url}
        </div>
        <Button
          type="button"
          onClick={copy}
          className="h-auto px-4 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl shrink-0"
          data-testid={`${testid}-copy-button`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default function Home({ freeToken = null, freeExpiry = null }) {
  const location = useLocation();
  const stateTemplate = location.state?.template;

  const [occasions, setOccasions] = useState([]);
  const [personName, setPersonName] = useState("");
  const [giverName, setGiverName] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");
  const [closesAt, setClosesAt] = useState(MAX_STR);
  const [occasionTitle, setOccasionTitle] = useState("");
  const [occasionDescription, setOccasionDescription] = useState("");
  const [questions, setQuestions] = useState([""]);
  const [sourceOccasionId, setSourceOccasionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverMsg, setRecoverMsg] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [fiGenitive, setFiGenitive] = useState("");
  const [fiAllative, setFiAllative] = useState("");
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsEdited, setFormsEdited] = useState(false);

  const origin = window.location.origin;

  useEffect(() => {
    api
      .get("/occasions/public")
      .then(({ data }) => {
        setOccasions(data);
        if (stateTemplate) {
          setOccasionTitle(stateTemplate.title || "");
          setOccasionDescription(stateTemplate.description || "");
          setQuestions(stateTemplate.questions?.length ? stateTemplate.questions : [""]);
          setSourceOccasionId(stateTemplate.source_occasion_id || null);
        } else {
          const bday = data.find((o) => o.title === "Syntymäpäivä") || data[0];
          if (bday) {
            setOccasionTitle(bday.title);
            setOccasionDescription(bday.description);
            setQuestions(bday.questions);
            setSourceOccasionId(bday.id);
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateQ = (i, val) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? val : q)));
  const addQ = () => setQuestions((qs) => (qs.length < 10 ? [...qs, ""] : qs));
  const removeQ = (i) => setQuestions((qs) => (qs.length > 1 ? qs.filter((_, idx) => idx !== i) : qs));
  const moveQ = (i, dir) =>
    setQuestions((qs) => {
      const j = i + dir;
      if (j < 0 || j >= qs.length) return qs;
      const c = [...qs];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });

  // Suggest the Finnish case forms once the organizer has entered a name.
  // Debounced; never overwrites a value the organizer has edited.
  useEffect(() => {
    const name = personName.trim();
    if (!name || formsEdited) return;
    const handle = setTimeout(async () => {
      setFormsLoading(true);
      try {
        const { data } = await api.post("/name-forms/preview", { name, langs: ["fi"] });
        const fi = (data && data.name_forms && data.name_forms.fi) || {};
        if (!formsEdited) {
          setFiGenitive(fi.genitive || "");
          setFiAllative(fi.allative || "");
        }
      } catch {
        /* leave fields empty; the server falls back to a case-free phrasing */
      } finally {
        setFormsLoading(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [personName, formsEdited]);

  const submit = async (e) => {
    e.preventDefault();
    if (!personName.trim() || !giverName.trim()) {
      toast.error(t("home.err.bothNames"));
      return;
    }
    if (!creatorEmail.trim()) {
      toast.error(t("home.err.email"));
      return;
    }
    if (!occasionTitle.trim()) {
      toast.error(t("home.err.occasionName"));
      return;
    }
    const cleaned = questions.map((q) => q.trim()).filter(Boolean);
    if (cleaned.length < 1) {
      toast.error(t("home.err.atLeastOneQuestion"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        person_name: personName,
        giver_name: giverName,
        creator_email: creatorEmail,
        closes_at_date: closesAt,
        occasion_title: occasionTitle,
        occasion_description: occasionDescription,
        questions,
        source_occasion_id: sourceOccasionId,
        free_token: freeToken,
      };
      if (personName.trim() && (fiGenitive.trim() || fiAllative.trim() || formsEdited)) {
        payload.fi_genitive = fiGenitive;
        payload.fi_allative = fiAllative;
      }
      const { data } = await api.post("/collections", payload);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("home.err.createFailed"));
    } finally {
      setLoading(false);
    }
  };

  const submitRecover = async (e) => {
    e.preventDefault();
    if (!recoverEmail.trim()) {
      toast.error(t("home.err.recoverEmail"));
      return;
    }
    setRecovering(true);
    try {
      const { data } = await api.post("/recover", { email: recoverEmail.trim() });
      setRecoverMsg(data.message);
    } catch (err) {
      toast.error(t("home.err.recoverFailed"));
    } finally {
      setRecovering(false);
    }
  };

  const chips = occasions.slice(0, 8);
  const selectedSubjectType = occasions.find((o) => o.id === sourceOccasionId)?.subject_type || "person";
  const freeDate = freeExpiry
    ? new Date(freeExpiry).toLocaleDateString("fi-FI", { timeZone: "Europe/Helsinki" })
    : "31.12.2026";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <nav className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 text-[#C86D51]">
            <Gift className="w-6 h-6" />
            <span className="text-sm font-semibold tracking-wide uppercase">Portwrite</span>
          </div>
          <div className="flex items-center gap-4">
            <Dialog onOpenChange={(o) => { if (!o) { setRecoverMsg(""); setRecoverEmail(""); } }}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="text-sm font-medium text-[#7A6B63] hover:text-[#B35A3E] hover:underline"
                  data-testid="recover-link-button"
                >
                  {t("home.recover.trigger")}
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-serif">{t("home.recover.title")}</DialogTitle>
                  <DialogDescription>
                    {t("home.recover.desc")}
                  </DialogDescription>
                </DialogHeader>
                {recoverMsg ? (
                  <div className="flex items-center gap-3 rounded-2xl bg-[#EAF1EA] border border-[#CBDDCB] p-4 text-[#3f6047]" data-testid="recover-message">
                    <Check className="w-5 h-5 shrink-0" />
                    <span className="text-sm">{recoverMsg}</span>
                  </div>
                ) : (
                  <form onSubmit={submitRecover} className="space-y-3" data-testid="recover-form">
                    <Input
                      type="email"
                      value={recoverEmail}
                      onChange={(e) => setRecoverEmail(e.target.value)}
                      placeholder={t("common.emailPlaceholder")}
                      className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                      data-testid="recover-email-input"
                    />
                    <Button
                      type="submit"
                      disabled={recovering}
                      className="w-full h-12 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                      data-testid="recover-submit-button"
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      {recovering ? t("common.sending") : t("home.recover.submit")}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            <Link
              to="/tilaisuudet"
              className="text-sm font-medium text-[#B35A3E] hover:underline"
              data-testid="nav-occasions-link"
            >
              {t("nav.occasions")}
            </Link>
          </div>
        </nav>

        {freeToken && (
          <div className="mb-6 rounded-2xl border border-[#CBE0CB] bg-[#EAF1EA] p-5 text-[#2f4a37]" data-testid="free-banner">
            <p className="text-sm leading-relaxed">
              {t("home.freeBanner", { date: freeDate })}
            </p>
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#2C221E] leading-tight">
            {t("home.hero.title")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#7A6B63] leading-relaxed">
            {t("home.hero.subtitle")}
          </p>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-8 rounded-3xl border border-[#E8DEC2] bg-white p-6 sm:p-8 shadow-sm"
          data-testid="sample-portrait-section"
        >
          <div className="text-xs font-semibold tracking-widest uppercase text-[#C86D51]">
            {t("home.sample.eyebrow")}
          </div>
          <p className="mt-4 font-serif text-[17px] leading-loose text-[#2C221E]" data-testid="sample-portrait-text">
            {t("home.sample.text")}
          </p>
          <p className="mt-4 text-sm text-[#7A6B63]" data-testid="sample-portrait-note">
            {t("home.sample.note")}
          </p>
        </motion.section>

        {!result ? (
          <>
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 rounded-3xl border border-[#E8DEC2] bg-white p-6 sm:p-8 shadow-sm space-y-6"
              data-testid="create-collection-form"
            >
              <div className="space-y-2">
                <Label htmlFor="person" className="text-[#2C221E] text-base">{selectedSubjectType === "community" ? t("home.form.subjectCommunity") : t("home.form.subjectPerson")}</Label>
                <Input
                  id="person"
                  value={personName}
                  onChange={(e) => setPersonName(e.target.value)}
                  placeholder={selectedSubjectType === "community" ? t("home.form.namePlaceholderCommunity") : t("home.form.namePlaceholderPerson")}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="person-name-input"
                />
              </div>

              {personName.trim() && (
                <div className="space-y-4 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-4" data-testid="name-forms-fields">
                  <div className="space-y-2">
                    <Label htmlFor="fi-genitive" className="text-[#2C221E] text-base">{t("nameForms.genitiveLabel")}</Label>
                    <Input
                      id="fi-genitive"
                      value={fiGenitive}
                      onChange={(e) => { setFiGenitive(e.target.value); setFormsEdited(true); }}
                      placeholder={formsLoading ? t("nameForms.generating") : ""}
                      className="h-12 rounded-xl border-[#E8DEC2] bg-white text-base"
                      data-testid="fi-genitive-input"
                    />
                    <p className="text-sm text-[#7A6B63]">{t("nameForms.genitiveHelp")}</p>
                    <p className="text-sm font-medium text-[#B35A3E]" data-testid="fi-genitive-preview">
                      {fiGenitive.trim()
                        ? t("nameForms.genitivePreview", { name: fiGenitive.trim() })
                        : t("nameForms.genitivePreviewFb", { name: personName.trim() })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fi-allative" className="text-[#2C221E] text-base">{t("nameForms.allativeLabel")}</Label>
                    <Input
                      id="fi-allative"
                      value={fiAllative}
                      onChange={(e) => { setFiAllative(e.target.value); setFormsEdited(true); }}
                      placeholder={formsLoading ? t("nameForms.generating") : ""}
                      className="h-12 rounded-xl border-[#E8DEC2] bg-white text-base"
                      data-testid="fi-allative-input"
                    />
                    <p className="text-sm text-[#7A6B63]">{t("nameForms.allativeHelp")}</p>
                    <p className="text-sm font-medium text-[#B35A3E]" data-testid="fi-allative-preview">
                      {fiAllative.trim()
                        ? t("nameForms.allativePreview", { name: fiAllative.trim() })
                        : t("nameForms.allativePreviewFb", { name: personName.trim() })}
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="giver" className="text-[#2C221E] text-base">{t("home.form.giver")}</Label>
                <Input
                  id="giver"
                  value={giverName}
                  onChange={(e) => setGiverName(e.target.value)}
                  placeholder={t("home.form.giverPlaceholder")}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="giver-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="creator-email" className="text-[#2C221E] text-base">{t("home.form.email")}</Label>
                <Input
                  id="creator-email"
                  type="email"
                  value={creatorEmail}
                  onChange={(e) => setCreatorEmail(e.target.value)}
                  placeholder={t("common.emailPlaceholder")}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="creator-email-input"
                />
                <p className="text-sm text-[#7A6B63]">
                  {t("home.form.emailHelp")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="occ-title" className="text-[#2C221E] text-base">{t("home.form.occasion")}</Label>
                <Input
                  id="occ-title"
                  value={occasionTitle}
                  onChange={(e) => setOccasionTitle(e.target.value)}
                  placeholder={t("home.form.occasionPlaceholder")}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="occasion-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="occ-desc" className="text-[#2C221E] text-base">{t("home.form.description")}</Label>
                <Input
                  id="occ-desc"
                  value={occasionDescription}
                  onChange={(e) => setOccasionDescription(e.target.value)}
                  placeholder={t("home.form.descriptionPlaceholder")}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="occasion-description-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[#2C221E] text-base">{t("home.form.questions", { n: questions.length })}</Label>
                </div>
                <p className="text-sm text-[#7A6B63]">
                  {t("home.form.nimiHintPre")} <code className="text-[#B35A3E]">{"{nimi}"}</code> {t("home.form.nimiHintPost")}
                </p>
                <div className="space-y-3" data-testid="questions-editor">
                  {questions.map((q, i) => (
                    <div key={i} className="rounded-2xl border border-[#E8DEC2] bg-[#FDFBF7] p-3" data-testid={`question-row-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className="mt-2 text-sm font-semibold text-[#C86D51] w-5 shrink-0">{i + 1}.</span>
                        <Textarea
                          value={q}
                          onChange={(e) => updateQ(i, e.target.value)}
                          placeholder={t("home.form.questionPlaceholder")}
                          className="min-h-[64px] rounded-xl border-[#E8DEC2] bg-white text-base resize-y flex-1"
                          data-testid={`question-input-${i}`}
                        />
                        <div className="flex flex-col gap-1 shrink-0">
                          <button type="button" onClick={() => moveQ(i, -1)} disabled={i === 0}
                            className="p-1.5 rounded-lg text-[#7A6B63] hover:bg-[#F0E8DA] disabled:opacity-30" data-testid={`question-up-${i}`}>
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => moveQ(i, 1)} disabled={i === questions.length - 1}
                            className="p-1.5 rounded-lg text-[#7A6B63] hover:bg-[#F0E8DA] disabled:opacity-30" data-testid={`question-down-${i}`}>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removeQ(i)} disabled={questions.length <= 1}
                            className="p-1.5 rounded-lg text-[#B84A39] hover:bg-[#FBECE7] disabled:opacity-30" data-testid={`question-remove-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {/\{nimi\}[a-zA-ZäöåÄÖÅ]/.test(q) && (
                        <p className="mt-2 ml-7 text-sm text-[#B84A39]" data-testid={`question-warning-${i}`}>
                          {t("home.form.nimiWarnPre")} <code>{"{nimi}"}</code> {t("home.form.nimiWarnPost")}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addQ}
                  disabled={questions.length >= 10}
                  className="w-full h-11 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
                  data-testid="add-question-button"
                >
                  <Plus className="w-4 h-4 mr-2" /> {t("home.form.addQuestion")}
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="closes" className="text-[#2C221E] text-base">{t("home.form.closesAt")}</Label>
                <Input
                  id="closes"
                  type="date"
                  value={closesAt}
                  min={TODAY_STR}
                  max={MAX_STR}
                  onChange={(e) => setClosesAt(e.target.value)}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="closes-at-input"
                />
                <p className="text-sm text-[#7A6B63]">{t("home.form.closesHelp")}</p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                data-testid="create-collection-submit-button"
              >
                {loading ? t("common.creating") : t("home.form.submit")}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </motion.form>

            {chips.length > 0 && (
              <div className="mt-10" data-testid="landing-occasions-section">
                <h2 className="font-serif text-2xl text-[#2C221E] mb-3">{t("home.usedFor")}</h2>
                <div className="flex flex-wrap gap-2">
                  {chips.map((o) => (
                    <Link
                      key={o.id}
                      to="/tilaisuudet"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F7F2EC] border border-[#E8DEC2] text-sm text-[#4A3E39] hover:bg-[#F8EBE6] transition-colors"
                      data-testid={`landing-chip-${o.id}`}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#C86D51]" />
                      {o.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 space-y-5"
            data-testid="create-collection-success"
          >
            <div className="rounded-3xl border border-[#E8DEC2] bg-white p-6 sm:p-8 shadow-sm">
              <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#2C221E]">
                {(() => {
                  const all = nameForm(result.name_forms, "fi", "allative");
                  return t(all ? "home.success.title" : "home.success.title.fb", { name: all || result.person_name });
                })()}
              </h2>
              <p className="mt-2 text-[#7A6B63]">{t("home.success.subtitle")}</p>
              {result.creator_email && (
                <p className="mt-1 text-sm text-[#7A6B63]" data-testid="links-sent-line">
                  {result.email_sent === false
                    ? t("home.success.emailFailed", { email: result.creator_email })
                    : t("home.success.emailSent", { email: result.creator_email })}
                </p>
              )}

              <div className="mt-6 space-y-4">
                <CopyField
                  label={t("home.success.publicLabel")}
                  hint={t("home.success.publicHint")}
                  url={`${origin}/v/${result.slug}`}
                  testid="public-link"
                />
                <CopyField
                  label={t("home.success.adminLabel")}
                  hint={t("home.success.adminHint")}
                  url={`${origin}/admin/${result.slug}/${result.admin_token}`}
                  testid="admin-link"
                  tone="admin"
                />
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#FBECE7] border border-[#E9C9BC] p-4" data-testid="admin-link-warning">
                <ShieldAlert className="w-5 h-5 text-[#B35A3E] shrink-0 mt-0.5" />
                <p className="text-sm text-[#8a4b38] leading-relaxed">
                  <strong>{t("home.success.warnStrong")}</strong> {t("home.success.warnRest")}
                </p>
              </div>

              <a
                href={`${origin}/admin/${result.slug}/${result.admin_token}`}
                className="mt-6 inline-flex items-center justify-center w-full h-12 rounded-xl border border-[#C86D51] text-[#B35A3E] font-medium hover:bg-[#F8EBE6] transition-colors"
                data-testid="go-to-admin-button"
              >
                {t("home.success.goAdmin")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}
